import React, { useEffect, useState } from 'react';
import { useInView } from './hooks';
import { LEAD_DAYS, TRIAL } from './data';

// "The cost of finding out late": when the signs appear, relative to the order stop.
// Illustrative (the churn pattern the source simulator plays); the trial band is measured.
const LANES = [
  { hue: 6, label: 'Replies slow down', from: -8 },
  { hue: 2, label: 'Promises slip, asks repeat', from: -6.5 },
  { hue: 5, label: 'Tone sours', from: -5 },
  { hue: 7, label: 'Orders narrow', from: -4 },
];

function useNarrow() {
  const [n, setN] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = () => setN(mq.matches); on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return n;
}

export function LateChart() {
  const narrow = useNarrow();
  const [ref, inView] = useInView();
  const W = narrow ? 380 : 640; const H = 300;
  const m = { l: 8, r: 12, t: 30, b: 44 };
  const x0 = -10; const x1 = 6;
  const x = (w) => m.l + ((w - x0) / (x1 - x0)) * (W - m.l - m.r);
  const laneH = 34; const lane0 = m.t + 18;
  const bandFrom = -TRIAL.p75Lead / 7; const med = -TRIAL.medianLead / 7;
  const report = 4.3;
  return (
    <figure className={`kz-viz kz-late ${inView ? 'is-in' : ''} ${narrow ? 'is-narrow' : ''}`} ref={ref}>
      <figcaption className="kz-viz-title">When the signs show up, and when most businesses find out</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label="Illustrative timeline: replies slow from about eight weeks before the order stop, promises slip from six and a half, tone sours from five, orders narrow from four. The order stop is at week zero; a monthly revenue report shows it about four weeks later. In the blind trial, the median alert came 15 days before the stop and the 75th percentile 39 days before.">
        <defs>
          {LANES.map((l) => (
            <linearGradient key={l.hue} id={`kz-lane-${l.hue}`} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={`var(--kz-h${l.hue})`} stopOpacity="0.08" />
              <stop offset="1" stopColor={`var(--kz-h${l.hue})`} stopOpacity="0.95" />
            </linearGradient>
          ))}
        </defs>
        <rect x={x(bandFrom)} y={m.t} width={x(0) - x(bandFrom)} height={H - m.t - m.b} className="kz-late-band" />
        <text x={x(bandFrom) + 6} y={m.t - 8} className="kz-viz-note kz-accent-1">Kizashi’s trial alerts</text>
        {(narrow ? [-8, -4, 0, 4] : [-10, -8, -6, -4, -2, 0, 2, 4, 6]).map((w) => (
          <g key={w}>
            <line x1={x(w)} x2={x(w)} y1={m.t} y2={H - m.b} className="kz-viz-grid" />
            <text x={x(w)} y={H - m.b + 16} textAnchor="middle" className="kz-viz-axis">{w === 0 ? '0' : `${w > 0 ? '+' : ''}${w}w`}</text>
          </g>
        ))}
        <line x1={x(med)} x2={x(med)} y1={m.t} y2={H - m.b} className="kz-late-med" />
        {LANES.map((l, i) => {
          const y = lane0 + i * (laneH + 8);
          return (
            <g key={l.label} className="kz-late-lane" style={{ '--d': `${i * 120}ms` }}>
              <rect x={x(l.from)} y={y} width={x(0) - x(l.from)} height={laneH} rx="6" fill={`url(#kz-lane-${l.hue})`} className="kz-late-bar" />
              <text x={x(l.from) + 10} y={y + laneH / 2 + 4} className="kz-late-label">{l.label}</text>
            </g>
          );
        })}
        <text x={x(med) - 4} y={H - m.b - 6} textAnchor="end" className="kz-viz-note kz-accent-1">{narrow ? 'median −15d' : 'median alert −15d'}</text>
        <line x1={x(0)} x2={x(0)} y1={m.t - 4} y2={H - m.b} className="kz-late-stop" />
        <text x={x(0) + 6} y={m.t + 6} className="kz-viz-note kz-accent-4">orders stop</text>
        <line x1={x(report)} x2={x(report)} y1={m.t - 4} y2={H - m.b} className="kz-late-report" />
        <text x={narrow ? x(report) + 4 : x(report) - 6} y={narrow ? m.t + 24 : m.t + 6} textAnchor={narrow ? 'start' : 'end'} className="kz-viz-note">{narrow ? 'report' : 'revenue report'}</text>
        <text x={W / 2} y={H - 6} textAnchor="middle" className="kz-viz-axis">{narrow ? 'weeks around the order stop' : 'weeks before and after the customer’s orders stop'}</text>
      </svg>
      <p className="kz-viz-foot">Illustrative: the churn pattern the source simulator plays. The shaded band and median are measured, from blind trial #5a.</p>
    </figure>
  );
}

// Trial #5a: every churner, placed by how many days before its order stop it was first alerted.
export function LeadChart() {
  const narrow = useNarrow();
  const [ref, inView] = useInView();
  const W = narrow ? 380 : 640; const H = 230;
  const m = { l: 14, r: 14, t: 34, b: 50 };
  const d0 = 50; const d1 = -40; // left = early
  const x = (d) => m.l + ((d0 - d) / (d0 - d1)) * (W - m.l - m.r);
  const rowY = m.t + 44; const missY = m.t + 104;
  const missed = TRIAL.churners - TRIAL.alerted;
  return (
    <figure className={`kz-viz kz-leadviz ${inView ? 'is-in' : ''} ${narrow ? 'is-narrow' : ''}`} ref={ref}>
      <figcaption className="kz-viz-title">All {TRIAL.churners} churners in trial #5a, by first alert</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Of ${TRIAL.churners} churners, ${TRIAL.early} were alerted before their orders stopped (up to 45 days before), ${TRIAL.alerted - TRIAL.early} after, and ${missed} were not alerted. Median lead of alerted churners 15 days.`}>
        <rect x={x(d0)} y={m.t} width={x(0) - x(d0)} height={H - m.t - m.b} className="kz-lead-early" />
        <text x={x(d0) + 6} y={m.t - 10} className="kz-viz-note kz-accent-1">alerted before the stop</text>
        <text x={x(0) + 6} y={m.t - 10} className="kz-viz-note kz-accent-4">after</text>
        {(narrow ? [40, 20, 0, -20, -40] : [50, 40, 30, 20, 10, 0, -10, -20, -30, -40]).map((d) => (
          <g key={d}>
            <line x1={x(d)} x2={x(d)} y1={m.t} y2={H - m.b} className={d === 0 ? 'kz-late-stop' : 'kz-viz-grid'} />
            <text x={x(d)} y={H - m.b + 16} textAnchor="middle" className="kz-viz-axis">{d > 0 ? `${d}d` : d === 0 ? 'stop' : `+${-d}d`}</text>
          </g>
        ))}
        <line x1={x(TRIAL.medianLead)} x2={x(TRIAL.medianLead)} y1={m.t + 14} y2={rowY + 22} className="kz-late-med" />
        <text x={x(TRIAL.medianLead)} y={m.t + 10} textAnchor="middle" className="kz-viz-note kz-accent-1">median 15d</text>
        {LEAD_DAYS.map((d, i) => (
          <circle key={`${d}-${i}`} cx={x(d)} cy={rowY + ((i % 2) ? 7 : -7)} r={narrow ? 6 : 7}
            className={`kz-lead-dot ${d > 0 ? 'is-early' : 'is-late'}`} style={{ '--d': `${i * 55}ms` }} />
        ))}
        <text x={m.l} y={missY - 16} className="kz-viz-axis">not alerted ({missed})</text>
        {Array.from({ length: missed }, (_, i) => (
          <circle key={i} cx={m.l + 8 + i * (narrow ? 15 : 19)} cy={missY} r={narrow ? 5.5 : 6.5} className="kz-lead-miss" style={{ '--d': `${900 + i * 30}ms` }} />
        ))}
        <text x={W / 2} y={H - 8} textAnchor="middle" className="kz-viz-axis">{narrow ? 'days from first alert to order stop' : 'days between the first alert and the order stop'}</text>
      </svg>
      <div className="kz-viz-legend">
        <span><i className="kz-key is-early" />alerted early ({TRIAL.early})</span>
        <span><i className="kz-key is-late" />alerted late ({TRIAL.alerted - TRIAL.early})</span>
        <span><i className="kz-key is-miss" />missed ({missed}, mostly silent churners)</span>
      </div>
    </figure>
  );
}
